import React, { useEffect, useState } from "react";
import { API_BASE_URL } from "src/config/apiBase";
import { decryptToken } from "../utils/getToken";
import { toast } from "react-toastify";
import { Plus, Trash2 } from "lucide-react";

const DEFAULT_STAGES = [
  { key: "", label: "Uncontacted", accent: "#6366f1" },
  { key: "Pending", label: "Pending", accent: "#3b82f6" },
  { key: "In Progress", label: "In Progress", accent: "#f59e0b" },
  { key: "Converted", label: "Converted", accent: "#10b981", isClosed: true },
  { key: "Not Interested", label: "Not Interested", accent: "#ef4444", isClosed: true },
];

const WEBHOOK_EVENTS = [
  "lead.created",
  "lead.updated",
  "lead.stage_changed",
  "lead.converted",
  "lead.assigned",
];

const DEFAULT_SEQUENCE = {
  enabled: false,
  steps: [
    { dayOffset: 0, hoursAfterCreate: 2, description: "First call" },
    { dayOffset: 1, hour: 10, minute: 0, description: "WhatsApp follow-up" },
    { dayOffset: 3, hour: 10, minute: 0, description: "Second call" },
  ],
};

const CrmPlatformSettings = () => {
  const [stages, setStages] = useState(DEFAULT_STAGES);
  const [webhooks, setWebhooks] = useState([]);
  const [sequence, setSequence] = useState(DEFAULT_SEQUENCE);
  const [savingStages, setSavingStages] = useState(false);
  const [savingHooks, setSavingHooks] = useState(false);
  const [savingSequence, setSavingSequence] = useState(false);
  const url = API_BASE_URL;
  const token = decryptToken("accessToken") || "";

  useEffect(() => {
    fetch(`${url}/leads/pipeline-stages`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (res?.stages?.length) setStages(res.stages);
      })
      .catch(() => {});

    fetch(`${url}/leads/outbound-webhooks`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (res?.webhooks) setWebhooks(res.webhooks);
      })
      .catch(() => {});

    fetch(`${url}/leads/follow-up-sequence`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((res) => {
        if (res?.sequence) setSequence(res.sequence);
      })
      .catch(() => {});
  }, [url, token]);

  const saveStages = async () => {
    setSavingStages(true);
    try {
      const res = await fetch(`${url}/leads/pipeline-stages`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ stages }),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setStages(data.stages || stages);
      toast.success("Pipeline stages saved");
    } catch {
      toast.error("Could not save pipeline stages");
    } finally {
      setSavingStages(false);
    }
  };

  const saveWebhooks = async () => {
    setSavingHooks(true);
    try {
      const res = await fetch(`${url}/leads/outbound-webhooks`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ webhooks }),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setWebhooks(data.webhooks || []);
      toast.success("Webhooks saved");
    } catch {
      toast.error("Could not save webhooks");
    } finally {
      setSavingHooks(false);
    }
  };

  const saveSequence = async () => {
    setSavingSequence(true);
    try {
      const res = await fetch(`${url}/leads/follow-up-sequence`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ sequence }),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      setSequence(data.sequence || sequence);
      toast.success("Follow-up sequence saved");
    } catch {
      toast.error("Could not save sequence");
    } finally {
      setSavingSequence(false);
    }
  };

  const updateStage = (index, field, value) => {
    setStages((prev) =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  };

  const addStage = () => {
    setStages((prev) => [
      ...prev,
      { key: `Stage ${prev.length + 1}`, label: "New stage", accent: "#8b5cf6" },
    ]);
  };

  const removeStage = (index) => {
    if (stages.length <= 2) {
      toast.warning("Keep at least two stages");
      return;
    }
    setStages((prev) => prev.filter((_, i) => i !== index));
  };

  const addWebhook = () => {
    setWebhooks((prev) => [
      ...prev,
      { label: "Zapier", url: "", events: ["lead.created"], active: true },
    ]);
  };

  const updateWebhook = (index, field, value) => {
    setWebhooks((prev) =>
      prev.map((w, i) => (i === index ? { ...w, [field]: value } : w))
    );
  };

  const toggleWebhookEvent = (index, event) => {
    setWebhooks((prev) =>
      prev.map((w, i) => {
        if (i !== index) return w;
        const events = w.events || [];
        const next = events.includes(event)
          ? events.filter((e) => e !== event)
          : [...events, event];
        return { ...w, events: next.length ? next : [event] };
      })
    );
  };

  return (
    <div className="crm-platform">
      <h3 className="crm-integrations__title">Platform settings</h3>
      <p className="crm-insights__muted" style={{ marginBottom: 16 }}>
        Configure your pipeline columns and outbound webhooks for Zapier, Make, or custom endpoints.
      </p>

      <section className="crm-platform__section">
        <div className="crm-integrations__head">
          <strong>Pipeline stages</strong>
          <button type="button" onClick={addStage}>
            <Plus size={14} /> Add stage
          </button>
        </div>
        <p className="crm-insights__muted" style={{ marginBottom: 8 }}>
          First stage uses an empty key for &quot;Uncontacted&quot;. Mark won/lost stages as closed.
        </p>
        {stages.map((stage, index) => (
          <div key={`stage-${index}`} className="crm-platform__stage-row">
            <input
              type="text"
              placeholder="Key (e.g. Pending)"
              value={stage.key}
              onChange={(e) => updateStage(index, "key", e.target.value)}
              className="crm-platform__input"
            />
            <input
              type="text"
              placeholder="Label"
              value={stage.label}
              onChange={(e) => updateStage(index, "label", e.target.value)}
              className="crm-platform__input"
            />
            <input
              type="color"
              value={stage.accent || "#6366f1"}
              onChange={(e) => updateStage(index, "accent", e.target.value)}
              title="Column accent"
            />
            <label className="crm-platform__closed">
              <input
                type="checkbox"
                checked={Boolean(stage.isClosed)}
                onChange={(e) => updateStage(index, "isClosed", e.target.checked)}
              />
              Closed
            </label>
            <button type="button" onClick={() => removeStage(index)} aria-label="Remove stage">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          className="crm-platform__save"
          onClick={saveStages}
          disabled={savingStages}
        >
          {savingStages ? "Saving…" : "Save pipeline"}
        </button>
      </section>

      <section className="crm-platform__section">
        <div className="crm-integrations__head">
          <strong>Outbound webhooks</strong>
          <button type="button" onClick={addWebhook}>
            <Plus size={14} /> Add webhook
          </button>
        </div>
        <p className="crm-insights__muted" style={{ marginBottom: 8 }}>
          POST JSON payloads when CRM events occur. Use with Zapier &quot;Webhooks by Zapier&quot; or Make custom webhook.
        </p>
        {webhooks.length === 0 && (
          <p className="crm-insights__muted">No webhooks configured yet.</p>
        )}
        {webhooks.map((hook, index) => (
          <div key={`hook-${index}`} className="crm-platform__hook-card">
            <div className="crm-platform__hook-head">
              <input
                type="text"
                placeholder="Label"
                value={hook.label || ""}
                onChange={(e) => updateWebhook(index, "label", e.target.value)}
                className="crm-platform__input"
              />
              <label>
                <input
                  type="checkbox"
                  checked={hook.active !== false}
                  onChange={(e) => updateWebhook(index, "active", e.target.checked)}
                />
                Active
              </label>
            </div>
            <input
              type="url"
              placeholder="https://hooks.zapier.com/..."
              value={hook.url || ""}
              onChange={(e) => updateWebhook(index, "url", e.target.value)}
              className="crm-platform__input crm-platform__input--wide"
            />
            <div className="crm-platform__events">
              {WEBHOOK_EVENTS.map((ev) => (
                <label key={ev}>
                  <input
                    type="checkbox"
                    checked={(hook.events || []).includes(ev)}
                    onChange={() => toggleWebhookEvent(index, ev)}
                  />
                  {ev}
                </label>
              ))}
            </div>
            <button type="button" onClick={() => setWebhooks((p) => p.filter((_, i) => i !== index))}>
              <Trash2 size={14} /> Remove
            </button>
          </div>
        ))}
        {webhooks.length > 0 && (
          <button
            type="button"
            className="crm-platform__save"
            onClick={saveWebhooks}
            disabled={savingHooks}
          >
            {savingHooks ? "Saving…" : "Save webhooks"}
          </button>
        )}
      </section>

      <section className="crm-platform__section">
        <div className="crm-integrations__head">
          <strong>Follow-up sequence</strong>
        </div>
        <p className="crm-insights__muted" style={{ marginBottom: 8 }}>
          Auto-schedule reminders on new leads. Each step runs when the previous reminder is acknowledged.
        </p>
        <label className="crm-automation__row">
          <input
            type="checkbox"
            checked={Boolean(sequence.enabled)}
            onChange={(e) => setSequence((s) => ({ ...s, enabled: e.target.checked }))}
          />
          <span>Enable follow-up sequence for new leads</span>
        </label>
        {(sequence.steps || []).map((step, index) => (
          <div key={`seq-${index}`} className="crm-platform__stage-row">
            <input
              type="number"
              min={0}
              placeholder="Day"
              value={step.dayOffset}
              onChange={(e) =>
                setSequence((s) => ({
                  ...s,
                  steps: s.steps.map((st, i) =>
                    i === index ? { ...st, dayOffset: Number(e.target.value) } : st
                  ),
                }))
              }
              className="crm-platform__input"
              style={{ maxWidth: 70 }}
            />
            <input
              type="text"
              placeholder="Description"
              value={step.description}
              onChange={(e) =>
                setSequence((s) => ({
                  ...s,
                  steps: s.steps.map((st, i) =>
                    i === index ? { ...st, description: e.target.value } : st
                  ),
                }))
              }
              className="crm-platform__input"
            />
            {step.dayOffset === 0 && (
              <input
                type="number"
                min={1}
                placeholder="Hours"
                value={step.hoursAfterCreate ?? 2}
                onChange={(e) =>
                  setSequence((s) => ({
                    ...s,
                    steps: s.steps.map((st, i) =>
                      i === index
                        ? { ...st, hoursAfterCreate: Number(e.target.value) }
                        : st
                    ),
                  }))
                }
                className="crm-platform__input"
                style={{ maxWidth: 80 }}
                title="Hours after lead created"
              />
            )}
          </div>
        ))}
        <button
          type="button"
          className="crm-platform__save"
          onClick={saveSequence}
          disabled={savingSequence}
        >
          {savingSequence ? "Saving…" : "Save sequence"}
        </button>
      </section>
    </div>
  );
};

export default CrmPlatformSettings;
export { DEFAULT_STAGES };
